import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useAppNavigate } from "../lib/route-primitives";
import { buildScanUrl, type WsMessage, type SlideState, type SlidePlaylistItem, type PresencePayload } from "@wedding/shared";
import { useWedding } from "../contexts/wedding-context";
import { formatWeddingDate } from "@wedding/shared";
import { useActiveEvent } from "../lib/wedding-event";
import { Loader2, LogOut, Maximize2, Minimize2, Pause } from "lucide-react";
import { cn } from "../lib/utils";
import { useFullscreen, usePrefersReducedMotion } from "../hooks/use-fullscreen";
import { useLiveReactions } from "../hooks/use-live-reactions";
import { useSlidePreload } from "../hooks/use-slide-preload";
import { CinematicStage } from "../components/slideshow/cinematic-stage";
import { FloatingHearts } from "../components/slideshow/floating-hearts";
import { CommentBubbles } from "../components/slideshow/comment-bubbles";
import { SocialTicker } from "../components/slideshow/social-ticker";
import { PresenceFacepile } from "@wedding/slideshow-ui";
import { JoinAnnouncements, useJoinAnnouncements } from "../components/slideshow/join-announcements";
import { JoinCta } from "../components/slideshow/join-cta";
import { IdleScreen } from "../components/slideshow/idle-screen";
import { SlideshowAudioControl } from "../components/slideshow/audio-control";

const CONTROLS_HIDE_MS = 3000;

/**
 * Synchronized slideshow — the SlideshowRoom DO is the conductor. Every
 * viewer receives the same `slide_state` (media, startedAt, duration, pause)
 * over the WebSocket, so all screens show the same moment. This client only
 * renders server state; it never advances slides on its own (a local timer
 * here would recreate the per-viewer desync this replaced).
 */
export function SlideshowPage() {
  const { t, i18n } = useTranslation();
  const { slug, wedding } = useWedding();
  const navigate = useAppNavigate();

  const reduceMotion = usePrefersReducedMotion();
  const { isFullscreen, toggle: toggleFullscreen } = useFullscreen();

  const [slideState, setSlideState] = useState<SlideState | null>(null);
  const [lastMedia, setLastMedia] = useState<SlidePlaylistItem | null>(null);
  const [connected, setConnected] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [presence, setPresence] = useState<PresencePayload | null>(null);
  // Local per-screen video audio — mute on/off only. Defaults MUTED every load
  // ("by default sessiz"); each screen decides for itself (no sync, no level).
  const [muted, setMuted] = useState(true);
  const { announcements, enqueue: enqueueJoins, remove: removeAnnouncement } = useJoinAnnouncements();
  // serverNow - Date.now(), refreshed on every slide_state → drift self-corrects.
  const clockOffsetRef = useRef(0);
  const reportedDurationsRef = useRef<Set<string>>(new Set());
  const wsRef = useRef<WebSocket | null>(null);
  const cancelledRef = useRef(false);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const wsAttemptRef = useRef(0);

  const { hearts, bubbles, reactionLabel, recentUploaders, removeHeart, removeBubble, seedReactions } =
    useLiveReactions(slug, reduceMotion);

  // ── WebSocket: server-driven slide state + viewer count ────
  const connectWs = useCallback(() => {
    if (cancelledRef.current) return;
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const ws = new WebSocket(`${protocol}//${window.location.host}/api/w/${slug}/slideshow/ws`);
    ws.onopen = () => {
      setConnected(true);
      wsAttemptRef.current = 0; // healthy — reset backoff
      // Keepalive ping (DO auto-replies "pong") so the idle socket isn't closed
      // by Cloudflare's edge → no reconnect storm draining the KV write budget.
      pingTimerRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send("ping");
      }, 45000);
    };
    ws.onclose = () => {
      // Freeze on the current slide until the reconnect snaps us back in sync.
      setConnected(false);
      if (pingTimerRef.current) { clearInterval(pingTimerRef.current); pingTimerRef.current = null; }
      if (!cancelledRef.current) {
        const delay = Math.min(60000, 3000 * 2 ** wsAttemptRef.current) * (0.8 + Math.random() * 0.4);
        wsAttemptRef.current += 1;
        reconnectTimerRef.current = setTimeout(connectWs, delay);
      }
    };
    ws.onmessage = (event) => {
      if (event.data === "pong") return; // keepalive ack
      const msg = JSON.parse(event.data) as WsMessage;
      if (msg.type === "slide_state" && msg.slideState) {
        clockOffsetRef.current = msg.slideState.serverNow - Date.now();
        if (msg.slideState.media) setLastMedia(msg.slideState.media);
        setSlideState(msg.slideState);
      }
      if (msg.type === "viewer_count" && msg.viewerCount !== undefined) {
        setViewerCount(msg.viewerCount);
      }
      if (msg.type === "presence" && msg.presence) {
        setPresence(msg.presence);
        if (msg.presence.joined?.length) enqueueJoins(msg.presence.joined);
      }
    };
    wsRef.current = ws;
  }, [slug, enqueueJoins]);

  useEffect(() => {
    cancelledRef.current = false;
    connectWs();
    return () => {
      cancelledRef.current = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (pingTimerRef.current) clearInterval(pingTimerRef.current);
      wsRef.current?.close();
    };
  }, [connectWs]);

  // Deliberate exits (tab close / navigation away) send a graceful WS close so
  // the audience count drops instantly instead of waiting for the edge idle
  // timeout. bfcache restores (iOS back-swipe) reconnect on pageshow.
  useEffect(() => {
    const onPageHide = () => {
      cancelledRef.current = true;
      wsRef.current?.close();
    };
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        cancelledRef.current = false;
        connectWs();
      }
    };
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [connectWs]);

  // ── Crowd-sourced video durations → sync server ────────────
  const sendDurationReport = useCallback((mediaId: string, durationMs: number) => {
    if (reportedDurationsRef.current.has(mediaId)) return;
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    reportedDurationsRef.current.add(mediaId);
    const msg: WsMessage = { type: "duration_report", mediaId, durationMs };
    ws.send(JSON.stringify(msg));
  }, []);

  // Preload the announced next slide (warms the CDN edge + probes durations).
  useSlidePreload(slideState?.next ?? null, sendDurationReport);

  // Lock background scroll + paint a dark surface while open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // During a title card the stage keeps the held media visible underneath.
  const current = slideState?.media ?? lastMedia;
  const interstitial = slideState?.slideType === "card";
  const paused = slideState?.paused ?? false;
  const total = slideState?.total ?? 0;
  const hasMedia = total > 0 && current !== null;
  const loadingInitial = slideState === null;

  // How far into the current slide we are on the SERVER clock — a late joiner
  // passes this to the stage so videos seek to the synchronized frame.
  const startOffsetMs = slideState
    ? Math.max(0, Date.now() + clockOffsetRef.current - slideState.startedAt)
    : 0;

  // ── Replay each photo's accumulated likes/comments as it appears ──
  const currentId = current?.id;
  const currentLikes = current?.like_count ?? 0;
  const currentComments = current?.comment_count ?? 0;
  useEffect(() => {
    if (!currentId) return;
    seedReactions({ id: currentId, like_count: currentLikes, comment_count: currentComments });
  }, [currentId, currentLikes, currentComments, seedReactions]);

  // ── Control layer: hidden until the surface is tapped, then auto-hides ──
  const [controlsVisible, setControlsVisible] = useState(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showControls = useCallback(() => {
    setControlsVisible(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setControlsVisible(false), CONTROLS_HIDE_MS);
  }, []);

  const handleToggleMute = useCallback(() => {
    setMuted((m) => !m);
    showControls(); // keep the control layer up after the tap
  }, [showControls]);

  useEffect(() => {
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  // ── Keyboard: f → fullscreen ──────────────────────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "f" || e.key === "F") {
        e.preventDefault();
        toggleFullscreen();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggleFullscreen]);

  // ── Exit: graceful WS close so the audience count drops instantly ──
  const handleExit = useCallback(() => {
    cancelledRef.current = true;
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    if (pingTimerRef.current) { clearInterval(pingTimerRef.current); pingTimerRef.current = null; }
    wsRef.current?.close();
    navigate(-1);
  }, [navigate]);

  const hideControls = useCallback(() => {
    setControlsVisible(false);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  }, []);

  // Slideshow o an YAŞANAN etkinlikte oynuyor → en yakın etkinliğin tarih/yeri
  // gösterilir (kına gecesi kınayı, düğün gecesi düğünü yazar).
  const activeEvent = useActiveEvent(wedding);
  const weddingDate = activeEvent
    ? formatWeddingDate(activeEvent.dateTime, i18n.language)
    : null;
  const dateLine = weddingDate
    ? `${weddingDate}${activeEvent?.location ? ` • ${activeEvent.location}` : ""}`
    : null;

  // Single source of truth (shared) — same join link as organizer settings/
  // dashboard, so every QR across the app points to the same page.
  // QR encodes the scan-redirect URL (/j/:slug) so slideshow scans are counted;
  // the worker logs the scan then 302s to the join page.
  const scanUrl = buildScanUrl(window.location.origin, slug);
  const title = wedding?.title ?? t("slideshow.title");

  return createPortal(
    <div
      className="fixed inset-0 z-[100] overflow-hidden bg-[#0b0a09] text-white select-none"
      onClick={showControls}
    >
      {/* ── Main surface ── */}
      {hasMedia ? (
        <CinematicStage
          current={current}
          slideMs={slideState?.durationMs ?? 6500}
          reduceMotion={reduceMotion}
          paused={paused}
          startOffsetMs={startOffsetMs}
          onVideoMeta={sendDurationReport}
          muted={muted}
        />
      ) : loadingInitial ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="h-10 w-10 animate-spin text-white/40" />
        </div>
      ) : (
        <IdleScreen title={title} dateLine={dateLine} url={scanUrl} coverUrl={wedding?.cover_photo_url ?? null} />
      )}

      {/* ── Live reaction overlays ── */}
      {hasMedia && (
        <>
          <FloatingHearts hearts={hearts} onDone={removeHeart} />
          <CommentBubbles bubbles={bubbles} onDone={removeBubble} />
          {/* Lower-left column: join announcements over the title block */}
          <div className="pointer-events-none absolute bottom-[3.5vmin] left-[3.5vmin] z-20 max-w-[55vw] md:max-w-[62vw] flex flex-col items-start gap-3">
            <JoinAnnouncements announcements={announcements} onDone={removeAnnouncement} />
            <SocialTicker
              title={title}
              dateLine={dateLine}
              uploaderName={current?.uploader_name ?? null}
              uploaderId={current?.uploader_guest_id ?? null}
              recentUploaders={recentUploaders}
              projector={isFullscreen}
              inline
            />
          </div>
          <JoinCta url={scanUrl} reactionLabel={reactionLabel} projector={isFullscreen} />
        </>
      )}

      {/* ── Title-card interstitial (server-scheduled, in sync everywhere) ── */}
      {interstitial && hasMedia && (
        <div className="absolute inset-0 z-[38] animate-in fade-in duration-700">
          <IdleScreen
            title={title}
            dateLine={dateLine}
            url={scanUrl}
            coverUrl={current?.url ?? wedding?.cover_photo_url ?? null}
          />
        </div>
      )}

      {/* ── Editorial frame counter (top-left, always on) ── */}
      {hasMedia && slideState && (
        <div className="absolute top-[3.5vmin] left-[3.5vmin] z-30 pointer-events-none">
          <p className="font-mono text-white/85 text-base md:text-lg tracking-[0.18em] tabular-nums drop-shadow-[0_1px_8px_rgba(0,0,0,0.7)]">
            {String(slideState.index + 1).padStart(3, "0")}
            <span className="text-white/40"> / {String(total).padStart(3, "0")}</span>
          </p>
        </div>
      )}

      {/* ── Top-right broadcast cluster (IG Live): [● CANLI] + audience facepile ── */}
      <div className="absolute top-[3vmin] right-[3vmin] z-50 flex items-center gap-2">
        {hasMedia && slideState && (
          <span
            className={cn(
              "flex items-center gap-1.5 rounded-full border px-2.5 py-1.5 text-[10px] md:text-xs font-bold uppercase tracking-[0.18em] backdrop-blur-md",
              paused
                ? "bg-amber-500/15 border-amber-400/30 text-amber-300"
                : connected
                  ? "bg-rose-500/15 border-rose-400/40 text-rose-200" // soft coral broadcast red
                  : "bg-white/10 border-white/20 text-white/60",
            )}
          >
            <span
              className={cn(
                "h-1.5 w-1.5 rounded-full",
                paused ? "bg-amber-400" : connected ? "bg-rose-400 ss-pulse" : "bg-white/40",
              )}
            />
            {paused ? t("slideshowRemote.paused") : connected ? t("slideshow.live") : t("slideshow.connecting")}
          </span>
        )}
        <PresenceFacepile presence={presence} />
      </div>

      {/* ── Centered control layer — tap the surface to reveal; auto-hides ── */}
      <div
        onClick={(e) => { e.stopPropagation(); hideControls(); }}
        className={cn(
          "absolute inset-0 z-[45] flex items-center justify-center bg-black/25 backdrop-blur-[2px] transition-opacity duration-500",
          controlsVisible ? "opacity-100" : "opacity-0 pointer-events-none",
        )}
      >
        <div className="flex items-center gap-8 sm:gap-10" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={toggleFullscreen}
            className="flex flex-col items-center gap-2 cursor-pointer group"
          >
            <span className="flex items-center justify-center size-14 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-white group-hover:bg-white/20 transition-colors group-focus-visible:ring-2 group-focus-visible:ring-white/60">
              {isFullscreen ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
            </span>
            <span className="text-xs font-medium text-white/70">
              {isFullscreen ? t("slideshow.exitFullscreen") : t("slideshow.fullscreen")}
            </span>
          </button>
          <SlideshowAudioControl muted={muted} onToggleMute={handleToggleMute} />
          <button
            onClick={handleExit}
            className="flex flex-col items-center gap-2 cursor-pointer group"
          >
            <span className="flex items-center justify-center size-14 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-white group-hover:bg-rose-500/25 group-hover:border-rose-400/40 transition-colors group-focus-visible:ring-2 group-focus-visible:ring-white/60">
              <LogOut className="h-5 w-5" />
            </span>
            <span className="text-xs font-medium text-white/70">{t("slideshow.exit")}</span>
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
